import { cx } from '@emotion/css';
import styled from '@emotion/styled';
import { Checkbox } from 'antd';
import dayjs from 'dayjs';
import { LocalMessage } from 'hooks/business/conversation';
import { useSingleOrBatchTrans } from 'hooks/business/im/useSingleOrBatchTrans';
import { noop, uniqBy } from 'lodash-es';
import { useMemo } from 'react';
import useMessageRender from 'views/Conversation/Chat/components/ChatMain/useMessageRender';
import { ChatProvider } from 'views/Conversation/Chat/contexts/ChatContext';
import { UIMessages, useUiMessage } from 'views/Conversation/Chat/useChatView';

interface MessagesProps {
  messages: LocalMessage[];
  conversation: Conversation.Conversation;
  selectedMessages: Conversation.Message[];
  onSelectedMessagesChange: (messages: Conversation.Message[]) => void;
}

interface MessageItemProps {
  checked?: boolean;
  message: UIMessages;
  conversation: Conversation.Conversation;
  onCheck?: (message: Conversation.Message, checked: boolean) => void;
}
const MessageItem = (props: MessageItemProps) => {
  const { checked, message, conversation, onCheck } = props;
  const { messageRender } = useMessageRender(conversation, []);

  const msgContent = messageRender(message);

  return (
    <MessageRow
      className={cx({ 'is-checked': checked })}
      onClick={(e) => {
        e.stopPropagation();
        onCheck?.(message.raw, !checked);
      }}
    >
      <span className="message-checkbox" onClick={(event) => event.stopPropagation()}>
        <Checkbox
          checked={!!checked}
          onChange={(event) => onCheck?.(message.raw, event.target.checked)}
          aria-label={
            message.raw.textContent?.text || dayjs(Number(message.raw.sendTime)).format('HH:mm')
          }
        />
      </span>
      {msgContent}
    </MessageRow>
  );
};

export const MessagesPanel = (props: MessagesProps) => {
  const { messages, conversation, selectedMessages, onSelectedMessagesChange } = props;
  const singleOrBatchTrans = useSingleOrBatchTrans();

  const { uiMessages } = useUiMessage({
    messages: messages || [],
    conversation,
  });

  const filteredMessages = useMemo(() => {
    return uiMessages.filter((item) => !!item);
  }, [uiMessages]);

  const contextValue = useMemo(() => {
    return {
      isGalaxy: true,
      dialogRef: null,
      setDialogRef: noop,
      clickQuote: noop,
      singleTransToSysLang: singleOrBatchTrans,
      showFeedback: noop,
      setContextMenuMessage: noop,
      setContextMenuSingleType: noop,
      showContextMenu: noop,
      videoTextState: undefined,
      setVideoTextState: noop,
      highlightMsg: undefined,
      hasQAPairMounted: false,
      setHasQAPairMounted: noop,
      hideFooter: true,
      showSourceContent: false,
    };
  }, [singleOrBatchTrans]);

  return (
    <ChatProvider value={contextValue}>
      <div className="flex flex-col gap-2 bg-[var(--fett-bg-app)] h-[300px] overflow-y-auto rounded-lg p-2">
        {filteredMessages.map((item) => {
          return (
            <MessageItem
              key={item.raw.msgId}
              message={item}
              conversation={conversation}
              checked={selectedMessages.some((message) => message.msgId === item.raw.msgId)}
              onCheck={(message, checked) => {
                if (checked) {
                  onSelectedMessagesChange(uniqBy([...selectedMessages, message], 'msgId'));
                } else {
                  onSelectedMessagesChange(
                    selectedMessages.filter((m) => m.msgId !== message.msgId),
                  );
                }
              }}
            />
          );
        })}
      </div>
    </ChatProvider>
  );
};

const MessageRow = styled.div`
  position: relative;
  display: flex;
  flex-shrink: 0;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 150ms, border-color 150ms;

  &:hover {
    border-color: var(--fett-border-strong);
    background: var(--fett-bg-subtle);
  }

  &.is-checked {
    border-color: var(--fett-text-tertiary);
    background: var(--fett-bg-panel);
  }

  > .message-checkbox {
    flex-shrink: 0;
    margin-top: 2px;
  }

  .ant-checkbox-wrapper {
    line-height: 20px;
  }

  .ant-checkbox {
    top: 0;
  }

  .ant-checkbox-inner {
    width: 20px;
    height: 20px;
    border: 2px solid var(--fett-text-tertiary);
    border-radius: 50%;
    background: var(--fett-bg-panel);
  }

  &:hover .ant-checkbox-inner,
  .ant-checkbox-input:focus + .ant-checkbox-inner {
    border-color: var(--fett-text-primary);
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    border-color: var(--fett-text-primary);
    background: var(--fett-text-primary);
  }

  .ant-checkbox-checked .ant-checkbox-inner::after {
    border-color: var(--fett-bg-panel);
  }

  .ant-checkbox::after {
    border-radius: 50%;
  }
`;
